import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// STAGE 2 · S2.1 — is Sales Management switched on, and which of the six Sales permissions does this person hold?
// Asked once per signed-in person. Switched off = every Sales door is absent (the server answers 404, Admin included).
let cached = { email: null, data: null, inflight: null };

export function loadSales(email, fresh = false) {
  if (!fresh && cached.email === email && cached.data) return Promise.resolve(cached.data);
  if (!fresh && cached.email === email && cached.inflight) return cached.inflight;
  cached = { email, data: null, inflight: null };
  cached.inflight = api.get("/sales/status")
    .then((r) => r.data).catch(() => ({ on: false, caps: {}, can_switch: false }))
    .then((d) => { if (cached.email === email) cached.data = d; return d; });
  return cached.inflight;
}

export function useSales() {
  const { user } = useAuth();
  const email = user?.email || null;
  const [s, setS] = useState(cached.email === email ? cached.data : null);
  useEffect(() => {
    let live = true;
    if (email) loadSales(email).then((d) => live && setS(d));
    return () => { live = false; };
  }, [email]);
  const caps = s?.caps || {};
  return {
    ready: !!s, on: !!s?.on, caps, canSwitch: !!s?.can_switch,
    // the six Sales permissions open Sales Management; Recon finished alone opens only a truck's Sales menu (S2.4)
    any: !!s?.on && Object.entries(caps).some(([k, v]) => v && k !== "finish_recon"),
    recon: !!s?.on && !!caps.finish_recon,
    reload: () => email && loadSales(email, true).then(setS),
  };
}

export async function undoSale(eventId, after) {
  try { const r = await api.post(`/sales/undo/${eventId}`); toast.success(r.data.message); after?.(); return true; }
  catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
}

// every Sales action answers with its event; the success toast carries the Undo (no "are you sure?"). Inside the
// Sales dialog the Undo sits in the dialog itself (a toast under a dialog can't be tapped), so its toast is plain.
export function saleDone(data, after, inDialog = false) {
  toast.success(data.message, {
    duration: inDialog ? 4000 : 10000,
    action: data.event_id && !inDialog ? { label: "Undo", onClick: () => undoSale(data.event_id, after) } : undefined,
  });
  after?.();
}

// answers the action's own reply (its message + event, for an Undo beside it), or null when refused
export async function saleAct(method, path, body, after, inDialog = false, onBar = false) {
  try {
    const { data } = await api.request({ method, url: `/sales${path}`, data: body });
    if (onBar) after?.(); else saleDone(data, after, inDialog);   // on a screen with an Undo bar, the bar says it
    return data;
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    return null;
  }
}

export const STATE_STYLE = {
  not_ready: "bg-amber-100 text-amber-900 border-amber-300",
  in_preparation: "bg-sky-50 text-sky-800 border-sky-200",
  waiting_approval: "bg-violet-50 text-violet-800 border-violet-200",
  published: "bg-emerald-50 text-emerald-800 border-emerald-200",
  hidden: "bg-slate-100 text-slate-700 border-slate-300",
  sold: "bg-slate-800 text-white border-slate-800",
};
